import { useEffect, useState } from "react";
import { api, ApiError, type FichaData } from "../api";
import type { Row } from "../types";
import { IconClose } from "../icons";
import { GRUPOS_COLABORADOR, mostrar } from "../colaborador-campos";

const TOM_CLS: Record<string, string> = {
  ok: "ficha-card-ok",
  warn: "ficha-card-warn",
  danger: "ficha-card-danger",
  neutral: "",
};

const STATUS_LABEL: Record<string, string> = {
  vazio: "sem registros",
  indisponivel: "indisponível",
};

/**
 * Ficha 360° do colaborador — consolida, numa tela só, tudo que os vários
 * sistemas sabem sobre a pessoa (RH, DP, Operação, Qualidade, Almoxarifado).
 * Consome GET /api/colaboradores/:id/ficha (agregador resiliente do RH).
 */
export default function FichaModal({ colaborador, onClose }: { colaborador: Row; onClose: () => void }) {
  const [data, setData] = useState<FichaData | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    api
      .ficha(colaborador.id)
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setErro(e instanceof ApiError ? e.message : "Erro ao carregar a ficha."));
    return () => {
      cancel = true;
    };
  }, [colaborador.id]);

  const c = data?.colaborador;

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ficha" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Ficha do colaborador</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {erro ? (
            <div className="form-error">{erro}</div>
          ) : !data ? (
            <div className="empty">Carregando ficha 360°…</div>
          ) : (
            <>
              <div className="ficha-perfil">
                <div className="ficha-avatar">{(c?.nome ?? "?").charAt(0).toUpperCase()}</div>
                <div>
                  <div className="ficha-nome">{c?.nome ?? colaborador.nome}</div>
                  <div className="ficha-sub">{[c?.cargo, c?.departamento, c?.status].filter(Boolean).join(" · ") || "—"}</div>
                  {c?.email && <div className="ficha-sub">{c.email}</div>}
                </div>
              </div>

              {(data._meta.indisponiveis.length > 0 || data._meta.casadosPorNome.length > 0) && (
                <div className="ficha-avisos">
                  {data._meta.indisponiveis.length > 0 && (
                    <span className="ficha-aviso ficha-aviso-warn">
                      Sistema(s) fora do ar: {data._meta.indisponiveis.join(", ")}
                    </span>
                  )}
                  {data._meta.casadosPorNome.length > 0 && (
                    <span className="ficha-aviso">
                      Vínculo por nome (confira divergências): {data._meta.casadosPorNome.join(", ")}
                    </span>
                  )}
                </div>
              )}

              {/* CADASTRO COMPLETO — tudo o que a planilha do RH mantinha
                  (documentos, filiação, endereço, banco), agrupado. Vem antes
                  dos blocos dos sistemas porque é a identificação da pessoa. */}
              <section className="ficha-bloco">
                <h4 className="ficha-bloco-titulo">Cadastro completo</h4>
                <div className="ficha-cadastro">
                  {GRUPOS_COLABORADOR.map((g) => {
                    const preenchidos = g.campos.filter(
                      (f) => c && c[f.campo] != null && String(c[f.campo]).trim() !== "",
                    );
                    if (preenchidos.length === 0) return null;
                    return (
                      <div key={g.titulo} className="ficha-grupo">
                        <div className="ficha-secao-titulo">{g.titulo}</div>
                        <dl>
                          {preenchidos.map((f) => (
                            <div key={f.campo}>
                              <dt>{f.rotulo}</dt>
                              <dd>{mostrar(c?.[f.campo], f.tipo)}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    );
                  })}
                </div>
              </section>

              {data.blocos.map((b) => (
                <section key={b.sistema} className="ficha-bloco">
                  <h4 className="ficha-bloco-titulo">
                    {b.titulo}
                    {b.status !== "ok" && <span className="ficha-bloco-status">{STATUS_LABEL[b.status] ?? b.status}</span>}
                    {b.casadoPor === "nome" && <span className="ficha-bloco-tag">por nome</span>}
                  </h4>

                  {b.status === "indisponivel" ? (
                    <div className="empty">Não foi possível consultar este sistema agora.</div>
                  ) : b.cards.length === 0 && b.secoes.length === 0 ? (
                    <div className="empty">Sem registros para este colaborador.</div>
                  ) : (
                    <>
                      {b.cards.length > 0 && (
                        <div className="ficha-cards">
                          {b.cards.map((k, i) => (
                            <div key={i} className={`ficha-card ${TOM_CLS[k.tom ?? "neutral"] ?? ""}`}>
                              <div className="ficha-card-valor">{k.valor}</div>
                              <div className="ficha-card-label">{k.label}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {b.secoes.map((s, i) => (
                        <div key={i} className="ficha-secao">
                          <div className="ficha-secao-titulo">{s.titulo}</div>
                          {s.linhas.length === 0 ? (
                            <div className="ficha-secao-vazia">{s.vazioMsg ?? "—"}</div>
                          ) : (
                            <div className="table-wrap">
                              <table className="table">
                                <thead>
                                  <tr>
                                    {s.colunas.map((col, j) => (
                                      <th key={j}>{col}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.linhas.map((linha, r) => (
                                    <tr key={r}>
                                      {linha.map((cel, k) => (
                                        <td key={k}>{cel === null || cel === "" ? "—" : String(cel)}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </section>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
